/**
 * AgentLayout — agent mode around the existing 3D stage (CONTRACT §10.4, §10.5):
 *
 *   CaseRail (left 280) | stage (Dashboard's, centre) | AgentStream (right 380)
 *                       WorkbenchDrawer (bottom, between rail and stream)
 *   overlay panels (z 40): CasePage, MemoryPanel, PolicyPanel, AuditView, the vertical's dossier
 *   NotificationToasts, StageCaption, RoleSwitcher
 *
 * Generic primitive (SPEC §11): no asset ids, labels, role names, copy or panels of a vertical in
 * here. They arrive as props (assetLabel, roleLabel, demoRoles, canEditPolicy, renderDossier,
 * caseRailHint, defaultCurrency, suspectRole), supplied by Dashboard. When `open` is false only the
 * notification toasts render, so proactive contact works in the normal view.
 *
 * Phone width (≤ 760 px, AgentLayout.css): the rail and the stream become sheets over the stage,
 * opened from the phone bar (`sheet` = 'rail' | 'stream' | null).
 *
 * Focus: everything that changes what "this" means (visible approval, draft selection, open panel,
 * open output) is reported through onFocusPatch; Dashboard folds it into buildFocus.
 * Keyboard fallback: Shift+F = use the recorded result for the current step (SPEC §7).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText, Brain, ScrollText, History, LogOut, RotateCcw, Square, WifiOff, List, Activity,
} from 'lucide-react';
import CaseRail from './CaseRail.jsx';
import AgentStream from './AgentStream.jsx';
import WorkbenchDrawer from './WorkbenchDrawer.jsx';
import CasePage from './CasePage.jsx';
import MemoryPanel from './MemoryPanel.jsx';
import PolicyPanel from './PolicyPanel.jsx';
import AuditView from './AuditView.jsx';
import NotificationToasts from './NotificationToasts.jsx';
import RoleSwitcher from './RoleSwitcher.jsx';
import StageCaption from './StageCaption.jsx';
import { selectCaseView, visibleNotifications } from './agentReducer.js';
import { createReplay, replayFor, loadReplayEvents } from './agentReplay.js';
import { blockingCaseId, draftIdFromPath, replayBusy, scenarioClockText } from './agentUi.js';
import './AgentLayout.css';

const REPLAY_SPEED = 4;
const REPLAY_MAX_GAP_MS = 3000;
const ERROR_SHOW_MS = 6000;
const REPLAY_START_LOCK_MS = 15000;   // longest the button waits for the new case to appear
const EMPTY = Object.freeze({});

const PANEL_BUTTONS = [
  { key: 'case_page', label: 'Case page', Icon: FileText },
  { key: 'memory', label: 'Memory', Icon: Brain },
  { key: 'rules', label: 'Rules', Icon: ScrollText },
  { key: 'audit', label: 'Audit', Icon: History },
];

const isTyping = (el) => Boolean(el) && (
  el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable
);

/** Caption over the stage plus the ticking scenario clock for schedule cases. */
function StageOverlay({ caseObj, lastUi, specialist }) {
  const [now, setNow] = useState(() => Date.now());
  const schedule = caseObj?.trigger?.kind === 'schedule';
  useEffect(() => {
    if (!schedule) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [schedule]);
  const caption = lastUi?.caption ? { text: lastUi.caption, key: lastUi.seq } : null;
  return (
    <StageCaption
      caption={caption}
      specialist={specialist}
      scenarioClock={schedule ? scenarioClockText(caseObj, now) : null}
    />
  );
}

export default function AgentLayout({
  agent,
  open = false,
  onExit,
  stageAsset = null,
  onSelectPart,
  voice = null,
  assetLabel = (id) => id,
  roleLabel = (role) => String(role || '').replace(/_/g, ' '),
  /**
   * The vertical's part dossier for wb.panel 'dossier' (citation chip or voice):
   * renderDossier({partId, onSelect(id), onClose}) -> node. Without it the dossier panel is not shown.
   */
  renderDossier = null,
  /** CaseRail empty-list hint (the vertical's wording, e.g. its wake phrase). */
  caseRailHint,
  /** Currency for approval amounts whose data names none. */
  defaultCurrency = null,
  /** Role badge of a ranked suspect on the case page: (role) -> {color, icon, label}. */
  suspectRole,
  onFocusPatch,
  /** Called after a notification's deep link set the active case (Dashboard enters agent mode). */
  onOpenCase,
  /** Demo roles offered by the RoleSwitcher (auth off only). */
  demoRoles = [],
  /** The principal may enable or disable policy rules (site admin). */
  canEditPolicy = false,
  /** Replay speed for "Start line replay" (1 = real time). */
  replaySpeed = 1,
}) {
  const state = agent?.state;
  const caseId = agent?.activeCaseId ?? null;
  const specialists = state?.specialists ?? EMPTY;
  const live = useMemo(() => (state ? selectCaseView(state, caseId) : null), [state, caseId]);
  const wb = state?.wb;
  const panel = wb?.panel ?? null;

  const focusRef = useRef(onFocusPatch);
  const selectPartRef = useRef(onSelectPart);
  useEffect(() => { focusRef.current = onFocusPatch; selectPartRef.current = onSelectPart; });
  const patchFocus = useCallback((patch) => { focusRef.current?.(patch); }, []);

  // The active case as of the last render, read by async work that must not outlive a case switch.
  const caseIdRef = useRef(caseId);
  useEffect(() => { caseIdRef.current = caseId; }, [caseId]);

  /* ---- replay (read-only copy of the stream) ------------------------- */
  const [replay, setReplay] = useState(null);       // {caseId, state} while replaying
  const replayRef = useRef(null);
  const stopReplay = useCallback(() => {
    replayRef.current?.stop();
    replayRef.current = null;
    setReplay(null);
  }, []);
  useEffect(() => () => replayRef.current?.stop(), []);
  useEffect(() => { stopReplay(); }, [caseId, stopReplay]);

  // A replay whose stored events start after case.created still shows the case header.
  const shownReplay = replayFor(replay, caseId);
  const view = shownReplay
    ? (() => { const v = selectCaseView(shownReplay.state, caseId); return v.caseObj ? v : { ...v, caseObj: live?.caseObj ?? null }; })()
    : live;

  /* ---- scenarios for the rail ---------------------------------------- */
  const [scenarios, setScenarios] = useState([]);
  const scenariosFn = agent?.scenarios;
  useEffect(() => {
    if (!open || typeof scenariosFn !== 'function') return undefined;
    let alive = true;
    scenariosFn().then((r) => { if (alive) setScenarios(Array.isArray(r?.scenarios) ? r.scenarios : []); })
      .catch(() => { if (alive) setScenarios([]); });
    return () => { alive = false; };
  }, [open, scenariosFn]);

  /* ---- panels ---------------------------------------------------------- */
  const setPanel = useCallback((p) => agent?.setWb({ panel: p }), [agent]);
  useEffect(() => { patchFocus({ panel }); }, [panel, patchFocus]);

  // Dossier (citation chip, or wb.ui panel=dossier): select the part on the stage too.
  const partId = panel === 'dossier' ? wb?.partId ?? null : null;
  useEffect(() => { if (partId) selectPartRef.current?.(partId); }, [partId]);
  const openCitation = useCallback(({ cad_id: cadId } = {}) => {
    if (cadId) agent?.setWb({ panel: 'dossier', partId: cadId });
  }, [agent]);

  // Policy rules, loaded when the panel opens.
  const [policy, setPolicy] = useState({ rules: [], never: [], loading: false, error: null, busyId: null });
  const policyApi = agent?.policy;
  const loadPolicy = useCallback(() => {
    if (!policyApi) return;
    setPolicy((p) => ({ ...p, loading: true, error: null }));
    policyApi.list()
      .then((r) => setPolicy({ rules: r?.rules ?? [], never: r?.never ?? [], loading: false, error: null, busyId: null }))
      .catch((err) => setPolicy((p) => ({ ...p, loading: false, error: err.message, busyId: null })));
  }, [policyApi]);
  useEffect(() => { if (open && panel === 'rules') loadPolicy(); }, [open, panel, loadPolicy]);
  const togglePolicy = useCallback((id, enabled) => {
    if (!policyApi) return;
    setPolicy((p) => ({ ...p, busyId: id }));
    policyApi.patch(id, { enabled }).then(loadPolicy)
      .catch((err) => setPolicy((p) => ({ ...p, busyId: null, error: err.message })));
  }, [policyApi, loadPolicy]);

  // Audit entries, loaded when the panel opens (and refreshed as the case moves on).
  const [audit, setAudit] = useState({ entries: [], verified: null, loading: false, error: null });
  const auditFn = agent?.audit;
  const auditKey = `${caseId}|${live?.caseObj?.updated_at ?? ''}`;
  useEffect(() => {
    if (!open || panel !== 'audit' || !caseId || typeof auditFn !== 'function') return undefined;
    let alive = true;
    setAudit((a) => ({ ...a, loading: true, error: null }));
    auditFn(caseId)
      .then((r) => { if (alive) setAudit({ entries: r?.entries ?? [], verified: r?.verified ?? null, loading: false, error: null }); })
      .catch((err) => { if (alive) setAudit((a) => ({ ...a, loading: false, error: err.message })); });
    return () => { alive = false; };
  }, [open, panel, caseId, auditFn, auditKey]);

  const eventsFn = agent?.events;
  const startCaseReplay = useCallback(() => {
    if (!caseId || typeof eventsFn !== 'function') return;
    stopReplay();
    // A switch while the events load resolves null: the new live case must stay editable.
    loadReplayEvents(caseId, eventsFn, () => caseIdRef.current).then((events) => {
      if (!events) return;
      const r = createReplay(events, {
        speed: REPLAY_SPEED,
        maxGapMs: REPLAY_MAX_GAP_MS,
        onEvent: (s) => setReplay({ caseId, state: s }),
        onDone: () => { replayRef.current = null; },
      });
      replayRef.current = r;
      setReplay({ caseId, state: r.state });
      setPanel(null);
    }).catch((err) => setAudit((a) => ({ ...a, error: err.message })));
  }, [caseId, eventsFn, stopReplay, setPanel]);

  // Memory.
  const memAsset = live?.caseObj?.asset ?? stageAsset ?? null;
  const memItems = memAsset ? state?.memory?.[memAsset] ?? [] : [];
  const [memError, setMemError] = useState(null);
  // Edits rethrow so the item stays in edit mode; add and delete only report the error.
  const memWrap = (fn, rethrow = false) => (...args) => {
    setMemError(null);
    return Promise.resolve().then(() => fn(...args)).catch((err) => {
      setMemError(err.message);
      if (rethrow) throw err;
    });
  };

  // Case page view + share.
  const [pageView, setPageView] = useState('full');
  const shareFn = agent?.share;

  /* ---- drawer, drafts, approvals ------------------------------------- */
  const [editDraftId, setEditDraftId] = useState(null);
  // Another case's draft is not editable here (the reducer also clears wb.path on a switch).
  useEffect(() => { setEditDraftId(null); }, [caseId]);
  const openDraftFromPath = draftIdFromPath(wb?.path);
  useEffect(() => {
    if (!openDraftFromPath) return;
    setEditDraftId(openDraftFromPath);
    patchFocus({ draft: { id: openDraftFromPath, line_id: null, section_id: null } });
  }, [openDraftFromPath, patchFocus]);

  const openFile = useCallback((path) => {
    if (!path) return;
    agent?.setWb({ path, tab: path.startsWith('outputs/') ? 'outputs' : 'files', open: true, range: 'all' });
    const out = (live?.outputs ?? []).find((o) => o.path === path);
    patchFocus({ outputId: out?.id ?? null });
  }, [agent, live?.outputs, patchFocus]);

  const pendingId = live?.pendingApproval?.id ?? null;
  const onApprovalVisible = useCallback((draftId) => {
    patchFocus({
      draft: draftId ? { id: draftId, line_id: null, section_id: null } : null,
      approvalId: draftId ? pendingId : null,
    });
  }, [patchFocus, pendingId]);

  const onApprovalEdit = useCallback((approval) => {
    if (!approval?.draft_id) return;
    agent?.setWb({ tab: 'outputs', open: true, path: `outputs/drafts/${approval.draft_id}.json` });
    setEditDraftId(approval.draft_id);
    patchFocus({ draft: { id: approval.draft_id, line_id: null, section_id: null }, approvalId: approval.id });
  }, [agent, patchFocus]);

  /* ---- keyboard fallback: Shift+F = use the recorded result ----------- */
  const control = agent?.control;
  useEffect(() => {
    if (!open || typeof control !== 'function') return undefined;
    const onKey = (e) => {
      if (!e.shiftKey || e.ctrlKey || e.metaKey || e.altKey || e.key.toLowerCase() !== 'f') return;
      if (isTyping(e.target)) return;
      e.preventDefault();
      control('use_recorded');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, control]);

  /* ---- protocol errors (e.g. forbidden) shown briefly ----------------- */
  const lastError = state?.lastError;
  const [errorShown, setErrorShown] = useState(null);
  useEffect(() => {
    if (!lastError) return undefined;
    setErrorShown(lastError);
    if (blockingCaseId(state?.cases, lastError)) return undefined;   // has a Close action: stays until used or dismissed
    const timer = setTimeout(() => setErrorShown(null), ERROR_SHOW_MS);
    return () => clearTimeout(timer);
  }, [lastError]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- line replay: one at a time ----------------------------------- */
  // Disabled from the click until the new case appears (or the agent refuses, or 15 s pass), and while a
  // replay-opened case is still working. The agent enforces the same rule; this keeps the button honest.
  const [replayStarting, setReplayStarting] = useState(false);
  const agentCaseCount = Object.values(state?.cases ?? {}).filter((c) => c?.opened_by === 'agent').length;
  useEffect(() => { setReplayStarting(false); }, [agentCaseCount, lastError]);
  useEffect(() => {
    if (!replayStarting) return undefined;
    const timer = setTimeout(() => setReplayStarting(false), REPLAY_START_LOCK_MS);
    return () => clearTimeout(timer);
  }, [replayStarting]);
  const replayIsBusy = replayBusy(state?.cases, { starting: replayStarting });
  const startLineReplay = useCallback((scenario) => {
    const id = scenario || scenarios[0]?.id;
    if (!id || replayIsBusy) return;
    setReplayStarting(true);
    agent?.startReplay(id, replaySpeed);
  }, [agent, scenarios, replaySpeed, replayIsBusy]);

  /* ---- notifications -------------------------------------------------- */
  const openFromToast = useCallback((id, n) => {
    agent?.setActiveCase(id);
    if (n?.id) agent?.markRead(n.id);
    onOpenCase?.(id);
  }, [agent, onOpenCase]);

  /* ---- phone sheets (rail / stream) ------------------------------------ */
  const [sheet, setSheet] = useState(null);
  const toggleSheet = (name) => setSheet((cur) => (cur === name ? null : name));
  useEffect(() => { setSheet(null); }, [caseId]);
  const toastNotifications = useMemo(() => (state ? visibleNotifications(state) : []), [state]);

  if (!agent || !state) return null;

  const toasts = (
    <NotificationToasts
      notifications={toastNotifications}
      onOpen={openFromToast}
      onDismiss={(id) => agent.markRead(id)}
    />
  );
  // The toasts keep one position in the tree whether agent mode is open or not, so entering or
  // leaving agent mode does not remount them (and re-show toasts that had already hidden).
  if (!open) return <>{null}{toasts}</>;

  const caseObj = view?.caseObj ?? null;
  // Read only while the active case is the one being replayed (a stale replay never locks another case).
  const readOnly = Boolean(shownReplay);
  const voiceConfirm = voice?.confirm ?? null;
  const connected = agent.status === 'open';

  const toolbar = (
    <div className="agent-toolbar">
      <div className="agent-toolbar__panels" role="group" aria-label="Panels">
        {PANEL_BUTTONS.map((b) => {
          const PanelIcon = b.Icon;
          const on = panel === b.key;
          return (
            <button
              key={b.key}
              type="button"
              className={`agent-icon${on ? ' is-on' : ''}`}
              aria-pressed={on}
              title={b.label}
              aria-label={b.label}
              disabled={!caseId && b.key !== 'rules' && b.key !== 'memory'}
              onClick={() => setPanel(on ? null : b.key)}
            >
              <PanelIcon size={14} aria-hidden="true" />
            </button>
          );
        })}
      </div>
      <RoleSwitcher principal={state.principal} roles={demoRoles} roleLabel={roleLabel} onChange={(r) => agent.setRole(r)} />
      {!connected && (
        <span className="agent-toolbar__conn" title={`Agent connection: ${agent.status}`}>
          <WifiOff size={12} aria-hidden="true" />
          {agent.status === 'connecting' || agent.status === 'reconnecting' ? 'Connecting…' : 'Offline'}
          {(agent.status === 'closed' || agent.status === 'error') && (
            <button type="button" className="agent-icon" onClick={agent.reconnect} aria-label="Reconnect"><RotateCcw size={12} aria-hidden="true" /></button>
          )}
        </span>
      )}
      {onExit && (
        <button type="button" className="agent-icon agent-toolbar__exit" onClick={onExit} title="Leave agent mode" aria-label="Leave agent mode">
          <LogOut size={14} aria-hidden="true" />
        </button>
      )}
      {readOnly && (
        <div className="agent-toolbar__replay" role="status">
          Replaying the case at {REPLAY_SPEED}x (read only)
          <button type="button" className="agent-btn" onClick={stopReplay}><Square size={11} aria-hidden="true" /> Stop</button>
        </div>
      )}
      {errorShown && (
        <div className="agent-toolbar__error" role="alert">
          {errorShown.message || errorShown.code}
          {(() => {
            const id = blockingCaseId(state.cases, errorShown);
            return id ? (
              <button type="button" className="agent-btn" onClick={() => { control?.('close', { case_id: id }); setErrorShown(null); }}>
                <Square size={11} aria-hidden="true" /> Close {state.cases[id]?.ref || 'case'}
              </button>
            ) : null;
          })()}
          <button type="button" className="agent-btn" aria-label="Dismiss" onClick={() => setErrorShown(null)}>×</button>
        </div>
      )}
    </div>
  );

  const closePanel = () => setPanel(null);
  const escClose = (e) => { if (e.key === 'Escape') { e.stopPropagation(); closePanel(); } };

  return (
    <>
      <div className={`agent-root${sheet ? ` is-sheet-${sheet}` : ''}`}>
        <div className="agent-phonebar" role="group" aria-label="Agent panels">
          <button
            type="button"
            className={`agent-btn${sheet === 'rail' ? ' is-on' : ''}`}
            aria-pressed={sheet === 'rail'}
            onClick={() => toggleSheet('rail')}
          >
            <List size={13} aria-hidden="true" /> Cases
          </button>
          <button
            type="button"
            className={`agent-btn${sheet === 'stream' ? ' is-on' : ''}`}
            aria-pressed={sheet === 'stream'}
            onClick={() => toggleSheet('stream')}
          >
            <Activity size={13} aria-hidden="true" /> Activity
          </button>
        </div>

        <CaseRail
          cases={state.cases}
          order={state.order}
          activeCaseId={caseId}
          specialists={specialists}
          assetLabel={assetLabel}
          scenarios={scenarios}
          emptyHint={caseRailHint}
          onOpen={(id) => { agent.setActiveCase(id); setSheet(null); }}
          onStartReplay={startLineReplay}
          replayBusy={replayIsBusy}
        />

        <AgentStream
          caseObj={caseObj}
          plan={view?.plan}
          stream={view?.stream ?? []}
          drafts={view?.drafts ?? []}
          specialists={specialists}
          principal={state.principal}
          flashStep={view?.flashStep}
          voiceConfirm={voiceConfirm}
          roleLabel={roleLabel}
          assetLabel={assetLabel}
          narration={state.modes?.narration}
          readOnly={readOnly}
          defaultCurrency={defaultCurrency}
          header={toolbar}
          onIntent={(text) => agent.sendIntent(text)}
          onControl={(op, extra) => agent.control(op, extra)}
          onOpenCitation={openCitation}
          onDecide={(approvalId, decision, opts) => agent.decide(approvalId, decision, opts)}
          onOpenOutput={openFile}
          onOpenFile={openFile}
          onApprovalEdit={onApprovalEdit}
          onApprovalVisible={onApprovalVisible}
        />

        <WorkbenchDrawer
          wb={wb}
          files={view?.files ?? []}
          terminal={view?.terminal ?? []}
          browser={view?.browser ?? null}
          outputs={view?.outputs ?? []}
          drafts={view?.drafts ?? []}
          draftHighlight={(shownReplay ? shownReplay.state : state).draftHighlight}
          caseId={caseId}
          fileUrl={agent.fileUrl}
          editDraftId={editDraftId}
          onTab={(tab) => agent.setWb({ tab, open: true })}
          onToggle={(o) => agent.setWb({ open: o })}
          onOpenFile={openFile}
          onCloseFile={() => { agent.setWb({ path: null }); patchFocus({ outputId: null }); }}
          onDraftOp={(draftId, baseVersion, op) => { if (!readOnly) agent.draftOp(draftId, baseVersion, op); }}
          onDraftFocus={(draftId, lineId) => patchFocus({ draft: { id: draftId, line_id: lineId ?? null, section_id: null } })}
          onEditDraft={setEditDraftId}
        />

        <StageOverlay
          caseObj={caseObj}
          lastUi={caseId ? state.lastUiByCase?.[caseId] : null}
          specialist={specialists[caseObj?.specialist]}
        />

        {panel === 'case_page' && (
          <div className="agent-overlay" data-esc-guard="panel" onKeyDown={escClose}>
            <CasePage
              page={view?.page}
              comments={view?.comments ?? []}
              specialists={specialists}
              view={pageView}
              onViewChange={setPageView}
              fileUrl={caseId ? (path) => agent.fileUrl(caseId, path) : null}
              onComment={readOnly ? undefined : (body) => agent.comment(body)}
              onShare={caseId && typeof shareFn === 'function' ? () => shareFn(caseId) : undefined}
              onOpenCitation={openCitation}
              suspectRole={suspectRole}
              onClose={closePanel}
            />
          </div>
        )}

        {panel === 'memory' && (
          <div className="agent-overlay" data-esc-guard="panel" onKeyDown={escClose}>
            <MemoryPanel
              items={memItems}
              asset={memAsset}
              assetLabel={assetLabel}
              flash={state.memoryFlash}
              error={memError}
              readOnly={readOnly}
              onAdd={memWrap((text) => agent.memory.add(text, { asset: memAsset }))}
              onEdit={memWrap((id, text) => agent.memory.edit(id, text), true)}
              onDelete={memWrap((id) => agent.memory.remove(id))}
              onClose={closePanel}
            />
          </div>
        )}

        {panel === 'rules' && (
          <div className="agent-overlay" data-esc-guard="panel" onKeyDown={escClose}>
            <PolicyPanel
              rules={policy.rules}
              never={policy.never}
              canEdit={canEditPolicy}
              loading={policy.loading}
              error={policy.error}
              busyId={policy.busyId}
              onToggle={togglePolicy}
              onClose={closePanel}
            />
          </div>
        )}

        {panel === 'audit' && (
          <div className="agent-overlay" data-esc-guard="panel" onKeyDown={escClose}>
            <AuditView
              entries={audit.entries}
              verified={audit.verified}
              loading={audit.loading}
              error={audit.error}
              replaying={readOnly}
              onReplay={startCaseReplay}
              onStopReplay={stopReplay}
              onClose={closePanel}
            />
          </div>
        )}

        {panel === 'dossier' && typeof renderDossier === 'function' && (
          <div className="agent-dossier" data-esc-guard="panel" onKeyDown={escClose} aria-label="Part dossier">
            {renderDossier({
              partId,
              onSelect: (id) => { if (id) agent.setWb({ panel: 'dossier', partId: id }); },
              onClose: closePanel,
            })}
          </div>
        )}
      </div>
      {toasts}
    </>
  );
}
